import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ImageOff,
  Package2,
  PackageCheck,
  Plus,
  Search,
  Tags,
  RefreshCw
} from "lucide-react";
import api from "../api/api";
import { useDialog } from "../components/DialogProvider";
import { EmptyState, LoadingCard } from "../components/ui";
import { getKategoriFromKode } from "../utils/productCode";
import { includesSearch } from "../utils/search";

function ProductThumb({ imageUrl, alt }) {
  const [broken, setBroken] = useState(false);
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";

  const src = useMemo(() => {
    if (!imageUrl) return "";
    const raw = String(imageUrl);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw}`;
  }, [apiBase, imageUrl]);

  if (!src || broken) {
    return (
      <div className="flex h-11 w-11 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-zinc-400">
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-11 w-11 rounded-md border border-zinc-200 object-cover"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-lg border border-zinc-200 bg-white px-5 py-4 shadow-sm">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-100 bg-zinc-50">
        <Icon className="h-5 w-5 text-zinc-700" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-[11px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="mt-0.5 truncate text-2xl font-bold tracking-tight text-zinc-900 tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function CategoryBadge({ kode }) {
  const kategori = getKategoriFromKode(kode);
  const tone =
    kategori === "PRODUK UTAMA"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700 ring-emerald-600/20"
      : kategori === "ADDITIONAL ITEM"
        ? "border-indigo-200 bg-indigo-50 text-indigo-700 ring-indigo-600/20"
        : "border-amber-200 bg-amber-50 text-amber-800 ring-amber-600/20";

  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ring-inset ${tone}`}>{kategori || "-"}</span>;
}

function Products() {
  const dialog = useDialog();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingKode, setDeletingKode] = useState("");
  const [editingKode, setEditingKode] = useState("");
  const [editNama, setEditNama] = useState("");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editRemoveImage, setEditRemoveImage] = useState(false);
  const [savingKode, setSavingKode] = useState("");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const fetch = async () => {
    try {
      setLoading(true);
      const res = await api.get("/products");
      setProducts(res.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal mengambil data produk");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const startEdit = (p) => {
    const kode = String(p?.kode_barang || "").trim();
    if (!kode) return;
    setEditingKode(kode);
    setEditNama(String(p?.nama_barang || ""));
    setEditImageFile(null);
    setEditRemoveImage(false);
  };

  const cancelEdit = () => {
    setEditingKode("");
    setEditNama("");
    setEditImageFile(null);
    setEditRemoveImage(false);
    setSavingKode("");
  };

  const handleSave = async (p) => {
    const kode = String(p?.kode_barang || "").trim();
    const nama = String(editNama || "").trim();
    if (!kode) return;

    if (!nama) {
      dialog.toast({ type: "error", title: "Validasi", message: "Nama barang wajib diisi." });
      return;
    }

    try {
      setSavingKode(kode);
      setError("");

      const form = new FormData();
      form.append("nama_barang", nama);
      if (editImageFile) form.append("image", editImageFile);
      if (editRemoveImage && !editImageFile) form.append("remove_image", "1");

      await dialog.toastPromise(
        api.put(`/products/${encodeURIComponent(kode)}`, form),
        {
          loading: "Menyimpan perubahan…",
          success: (r) => r?.data?.message || `Produk diupdate: ${kode}.`,
          error: (e2) => e2?.response?.data?.message || e2.message || "Gagal update produk.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      cancelEdit();
      await fetch();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Gagal update produk";
      setError(msg);
    } finally {
      setSavingKode("");
    }
  };

  const handleDelete = async (p) => {
    const kode = String(p?.kode_barang || "").trim();
    const nama = String(p?.nama_barang || "").trim();
    if (!kode) return;

    const ok = await dialog.confirm(
      `Yakin mau hapus produk ini?\n\nKode: ${kode}${nama ? `\nNama: ${nama}` : ""}\n\nAksi ini tidak bisa dibatalkan.`,
      { title: "Hapus Produk", confirmText: "Hapus", cancelText: "Batal" }
    );
    if (!ok) return;

    try {
      setDeletingKode(kode);
      setError("");

      await dialog.toastPromise(
        api.delete(`/products/${encodeURIComponent(kode)}`),
        {
          loading: "Menghapus produk…",
          success: (r) => r?.data?.message || `Produk dihapus: ${kode}.`,
          error: (e2) => e2?.response?.data?.message || e2.message || "Gagal menghapus produk.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      await fetch();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Gagal menghapus produk";
      setError(msg);
    } finally {
      setDeletingKode("");
    }
  };

  const filteredProducts = useMemo(() => {
    const q = String(query || "").trim();
    if (!q) return products;
    return products.filter((p) => (
      includesSearch(
        `${p?.kode_barang || ""} ${p?.nama_barang || ""} ${getKategoriFromKode(p?.kode_barang) || ""}`,
        q
      )
    ));
  }, [products, query]);

  const productStats = useMemo(() => {
    return products.reduce(
      (acc, p) => {
        const kategori = getKategoriFromKode(p?.kode_barang);
        acc.total += 1;
        if (kategori === "PRODUK UTAMA") acc.utama += 1;
        if (p?.image_url) acc.withImage += 1;
        return acc;
      },
      { total: 0, utama: 0, withImage: 0 }
    );
  }, [products]);

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 shadow-sm">
            <Package2 className="h-3 w-3 text-sky-600" />
            Master Data
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-zinc-900">
            Daftar Produk
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Daftar master produk dengan tampilan yang lebih jelas, cepat dibaca, dan nyaman dipakai lama.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/products/new" className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
            <Plus className="h-4 w-4" />
            Tambah Produk
          </Link>
        </div>
      </header>

      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <MiniStat icon={Package2} label="Total Produk" value={productStats.total} />
        <MiniStat icon={PackageCheck} label="Produk Utama" value={productStats.utama} />
        <MiniStat icon={Tags} label="Dengan Foto" value={productStats.withImage} />
      </div>

      <div className="flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
        <div className="border-b border-zinc-100 bg-zinc-50/50 p-5">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
            <div>
              <div className="text-[15px] font-bold text-zinc-900">Daftar Produk</div>
              <div className="mt-1 text-[12px] font-medium text-zinc-500">
                Menampilkan {filteredProducts.length} dari {products.length} produk.
              </div>
            </div>
            <div className="relative w-full lg:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-md border border-zinc-300 bg-white py-2 pl-9 pr-4 text-[13px] outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                placeholder="Cari kode/nama, contoh: atsb2l"
              />
            </div>
          </div>
        </div>

        <div className="grid gap-4 p-4 md:hidden">
          {loading ? (
            <LoadingCard label="Memuat produk..." />
          ) : filteredProducts.length === 0 ? (
            <EmptyState
              icon={Package2}
              title={products.length === 0 ? "Belum ada data produk" : "Produk tidak ditemukan"}
              description={products.length === 0 ? "Tambahkan produk pertama agar master data inventory mulai terisi." : "Coba pakai kata kunci lain atau cek kode produk yang dicari."}
              action={<Link to="/products/new" className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors">Tambah Produk</Link>}
            />
          ) : (
            filteredProducts.map((p) => (
              <div key={p.id} className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm hover:border-zinc-300 transition-colors">
                <div className="flex items-start gap-4">
                  <ProductThumb imageUrl={p.image_url} alt={p.nama_barang || p.kode_barang || "Produk"} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <div className="font-mono text-[11px] font-bold text-zinc-500 uppercase tracking-widest">{p.kode_barang}</div>
                      <CategoryBadge kode={p.kode_barang} />
                    </div>
                    {editingKode === p.kode_barang ? (
                      <div className="mt-2 space-y-3">
                        <input
                          value={editNama}
                          onChange={(e) => setEditNama(e.target.value)}
                          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                          placeholder="Nama barang"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setEditImageFile(f);
                            if (f) setEditRemoveImage(false);
                          }}
                          className="block w-full text-[11px] text-zinc-700 file:mr-3 file:rounded file:border file:border-zinc-200 file:bg-zinc-50 file:px-3 file:py-1.5 file:text-[11px] file:font-bold file:text-zinc-700"
                        />
                        <label className="flex items-center gap-2 text-[12px] font-semibold text-zinc-700">
                          <input
                            type="checkbox"
                            checked={editRemoveImage}
                            onChange={(e) => {
                              setEditRemoveImage(e.target.checked);
                              if (e.target.checked) setEditImageFile(null);
                            }}
                            className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                          />
                          Hapus foto
                        </label>
                      </div>
                    ) : (
                      <div className="text-[14px] font-bold leading-tight text-zinc-900">{p.nama_barang}</div>
                    )}
                  </div>
                </div>

                {editingKode === p.kode_barang ? (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button onClick={() => handleSave(p)} disabled={savingKode === p.kode_barang} className="rounded-md bg-zinc-900 py-2 text-[12px] font-bold text-white shadow-sm hover:bg-zinc-800 disabled:opacity-50">
                      {savingKode === p.kode_barang ? "Menyimpan..." : "Simpan"}
                    </button>
                    <button onClick={cancelEdit} disabled={savingKode === p.kode_barang} className="rounded-md border border-zinc-200 bg-white py-2 text-[12px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50">
                      Batal
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button onClick={() => startEdit(p)} disabled={deletingKode === p.kode_barang} className="rounded-md border border-zinc-200 bg-white py-2 text-[12px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(p)} disabled={deletingKode === p.kode_barang} className="rounded-md border border-rose-200 bg-rose-50 py-2 text-[12px] font-bold text-rose-700 shadow-sm hover:bg-rose-100">
                      {deletingKode === p.kode_barang ? "Menghapus..." : "Hapus"}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="hidden md:block overflow-x-auto bg-white">
          <table className="min-w-full text-left text-[13px]">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
              <tr>
                <th className="px-5 py-3 font-medium w-16 text-center">Foto</th>
                <th className="px-5 py-3 font-medium">Kode & Kategori</th>
                <th className="px-5 py-3 font-medium">Nama Produk</th>
                <th className="px-5 py-3 font-medium text-right w-48">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading ? (
                <tr>
                  <td className="px-5 py-8 text-center text-zinc-600" colSpan={4}>
                    <div className="flex flex-col items-center gap-3">
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      Memuat produk...
                    </div>
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td className="px-5 py-8 text-center text-zinc-600" colSpan={4}>
                    <EmptyState
                      icon={Package2}
                      title={products.length === 0 ? "Belum ada data produk" : "Produk tidak ditemukan"}
                      description={products.length === 0 ? "Tambahkan produk pertama agar master data inventory mulai terisi." : "Coba pakai kata kunci lain atau cek kode produk yang dicari."}
                      action={<Link to="/products/new" className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors">Tambah Produk</Link>}
                    />
                  </td>
                </tr>
              ) : (
                filteredProducts.map((p) => (
                  <tr key={p.id} className="hover:bg-zinc-50/50 transition-colors">
                    <td className="px-5 py-3 text-center">
                      <ProductThumb imageUrl={p.image_url} alt={p.nama_barang || p.kode_barang || "Produk"} />
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-mono text-[12px] font-bold text-zinc-900 mb-1.5">{p.kode_barang}</div>
                      <CategoryBadge kode={p.kode_barang} />
                    </td>
                    <td className="px-5 py-3">
                      {editingKode === p.kode_barang ? (
                        <div className="space-y-3 max-w-sm">
                          <input
                            value={editNama}
                            onChange={(e) => setEditNama(e.target.value)}
                            className="w-full rounded-md border border-zinc-300 px-3 py-2 text-[13px] outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                            placeholder="Nama barang"
                          />

                          <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const f = e.target.files?.[0] || null;
                                setEditImageFile(f);
                                if (f) setEditRemoveImage(false);
                              }}
                              className="block w-full text-[11px] text-zinc-700 file:mr-3 file:rounded file:border file:border-zinc-200 file:bg-zinc-50 file:px-2.5 file:py-1 file:text-[11px] file:font-bold file:text-zinc-700 hover:file:bg-zinc-100"
                            />

                            <label className="inline-flex items-center gap-2 text-[11px] font-bold text-zinc-700 shrink-0">
                              <input
                                type="checkbox"
                                checked={editRemoveImage}
                                onChange={(e) => {
                                  setEditRemoveImage(e.target.checked);
                                  if (e.target.checked) setEditImageFile(null);
                                }}
                                className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                              />
                              Hapus foto
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className="text-[13px] font-bold text-zinc-900">{p.nama_barang}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {editingKode === p.kode_barang ? (
                        <div className="inline-flex items-center justify-end gap-2 w-full">
                          <button onClick={() => handleSave(p)} disabled={savingKode === p.kode_barang} className="rounded-md bg-zinc-900 px-3 py-1.5 text-[12px] font-bold text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50">
                            <span className="inline-flex items-center gap-2">
                              {savingKode === p.kode_barang ? <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/60 border-t-white" /> : null}
                              {savingKode === p.kode_barang ? "Menyimpan..." : "Simpan"}
                            </span>
                          </button>
                          <button onClick={cancelEdit} disabled={savingKode === p.kode_barang} className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[12px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors">
                            Batal
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center justify-end gap-2 w-full">
                          <button onClick={() => startEdit(p)} disabled={deletingKode === p.kode_barang} className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[12px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors">
                            Edit
                          </button>
                          <button onClick={() => handleDelete(p)} disabled={deletingKode === p.kode_barang} className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-[12px] font-bold text-rose-700 shadow-sm hover:bg-rose-100 transition-colors">
                            <span className="inline-flex items-center gap-2">
                              {deletingKode === p.kode_barang ? <span className="h-3 w-3 animate-spin rounded-full border-2 border-rose-700/30 border-t-rose-700" /> : null}
                              {deletingKode === p.kode_barang ? "Menghapus..." : "Hapus"}
                            </span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Products;
